import { Card, CardContent } from "../../ui/card"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { useMemo } from "react"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { DataTable } from "../../ui/data-table/data-table"
import { useEcommSales } from "../../e-commerce/hooks/useSalesEcomm"
import { formatCurrency } from "../../../utils/formatCurrency"
import { formatNumber } from "../../../utils/formatNumber"


interface SKUPerformanceProps {
  selectedMonth: string;
}


interface SKUPerformanceMetrics {
  sku: string;
  product_name: string;
  units_sold: number;
  average_price: number;
  revenue: number;
}

export function RetailSKUPerformance({ selectedMonth }: SKUPerformanceProps) {

  const monthDate = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [selectedMonth]);

  const startDate = format(startOfMonth(monthDate), "yyyy-MM-dd");
  const endDate = format(endOfMonth(monthDate), "yyyy-MM-dd");


  const { data, isLoading } = useEcommSales(startDate, endDate);

  const shopifyData: SKUPerformanceMetrics[] = useMemo(() =>
    (data?.shopify_sku_performance || []).map((cat) => ({
      sku: cat.sku,
      product_name: cat.product_name,
      units_sold: cat.units_sold,
      average_price: cat.average_price,
      revenue: cat.revenue,    
    }))
    , [data]);

  const noData = shopifyData.length === 0;



  const columns = [
  {
    accessorKey: "sku",
    header: "SKU",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "product_name",
    header: "Product Name",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "average_price",
    header: "Average Price",
    isNumber: true,
    cell: (info: any) => formatCurrency(info.getValue()),
  },
  {
    accessorKey: "units_sold",
    header: "Units Sold",
    isNumber: true,
    cell: (info: any) => formatNumber(info.getValue()),
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    isNumber: true,
    cell: (info: any) => formatCurrency(info.getValue()),
  },
];


  const colors = useChartColors("ecommerce")
  if (!colors) return null;
  return (
    <>
      <SummaryHeader
        title="SKU"
      />
      <Card>
        <CardContent>
                {isLoading ? (
                  <SalesLoader variant="performance-channel" />
                ) : noData ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No Data Available
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={shopifyData}
                    searchColumn="sku"
                    searchPlaceholder="Search by SKU..."
                    showRowsPerPage={false}
                    initialPageSize={5}
                    showTotal={true} // Enable totals row
                    totalColumns={["revenue"]} // Columns to total
                  />
                )}        
        </CardContent>
      </Card>
    </>
  )
}

